import { gql } from '@apollo/client';
import React from 'react';
import SelectEntityOption from '../components/SelectEntityOption';
import type { TopicSelectOption_TopicFragment } from '../graphql/generated/graphqlTypes';
import LocalSearchHighlight from '../search/LocalSearchHighlight';
import { TopicMatchKey } from '../search/entitySearch/localEntitySearch';
import addFragments from '../utils/addFragments';
import TopicBreadcrumbs from './TopicBreadcrumbs';
import TopicIcon from './TopicIcon';
import TopicNameWithIcon from './TopicNameWithIcon';
import type { FuzzySearchMatch } from '../utils/search/fuzzySearch';

const TOPIC_FRAGMENT = gql`
  fragment TopicSelectOption_topic on Topic {
    id
    name
    ancestors {
      name
      ...TopicBreadcrumbs_topics
    }
    ...TopicNameWithIcon_topic
  }
  ${TopicBreadcrumbs.fragments.topics}
  ${TopicNameWithIcon.fragments.topic}
`;

interface TopicSelectOptionProps {
  name?: React.ReactNode;
  topic: TopicSelectOption_TopicFragment;
  matches?: readonly FuzzySearchMatch[];
  hideIcon?: boolean;
  focused?: boolean;
  truncate?: boolean;
  secondary?: React.ReactNode;
  secondaryTop?: React.ReactNode;
}

const TopicSelectOption = React.forwardRef<
  HTMLDivElement,
  TopicSelectOptionProps
>(
  (
    {
      name,
      topic,
      matches,
      hideIcon,
      focused,
      truncate,
      secondaryTop,
      ...props
    }: TopicSelectOptionProps,
    ref,
  ) => (
    <SelectEntityOption
      {...props}
      icon={!hideIcon && <TopicIcon bordered={focused} />}
      secondaryTop={
        secondaryTop ??
        (topic.ancestors && topic.ancestors.length > 0 && (
          <TopicBreadcrumbs
            names={topic.ancestors.map((ancestor, index) => (
              <LocalSearchHighlight
                matchKey={TopicMatchKey.AncestorNames}
                refIndex={index}
                matches={matches}
                defaultValue={ancestor.name}
              />
            ))}
            topics={topic.ancestors}
            size="small"
            truncate={truncate}
          />
        ))
      }
      label={
        <TopicNameWithIcon
          name={
            <LocalSearchHighlight
              matches={matches}
              matchKey={TopicMatchKey.Name}
              defaultValue={name || topic.name}
            />
          }
          topic={topic}
        />
      }
      ref={ref}
    />
  ),
);

export default addFragments(TopicSelectOption, { topic: TOPIC_FRAGMENT });
